import React from 'react';

import { usePermissionModal } from '$/components/Permission';

import { useMe } from '$/hooks/useMe';
import { hasPermission } from '$/utils/hasPermission';
import { rolesPermissionsMatrix } from '$/utils/pathConfig';
import { EWebinarSetGroup, HomePageSetFragment } from '$/GraphQL/schema/types';
import { navigate } from '@reach/router';

export enum HomePageModalType {
  ShareAll = 'ShareAll',
  Share = 'Share',
  Widget = 'Widget',
  CloneWebinar = 'CloneWebinar',
}

type HomePageModal =
  | {
      type: HomePageModalType.ShareAll;
    }
  | {
      type: HomePageModalType.Share;
      setId: string;
    }
  | {
      type: HomePageModalType.Widget;
      setId: string;
    }
  | {
      type: HomePageModalType.CloneWebinar;
      setId: string;
    };

interface HomePageContextValue {
  allSets: HomePageSetFragment[];
  filteredSets: HomePageSetFragment[];
  loadedSets: Record<string, boolean>;
  onSetLoaded: (setId: string) => void;
  noPublishedWebinars: number;
  modal?: HomePageModal;
  closeModal: () => void;
  openShareAllModal: () => void;
  openShareModal: (setId: string) => void;
  openWidgetModal: (setId: string) => void;
  openCloneWidgetModal: (setId: string) => void;
}

const HomePageContext = React.createContext<HomePageContextValue>({} as HomePageContextValue);

export const HomePageContextProvider: React.FC<{
  allSets: HomePageSetFragment[];
  filteredSets: HomePageSetFragment[];
  children: React.ReactNode;
}> = ({ allSets, filteredSets, children }) => {
  const me = useMe();
  const [modal, toggleModal] = React.useState<HomePageModal | undefined>(undefined);
  const [loadedSets, setLoadedSets] = React.useState<Record<string, boolean>>({});
  const { showPermissionModal } = usePermissionModal();

  const openShareAllModal = (): void => {
    if (!hasPermission(rolesPermissionsMatrix.shareEwebinar.path, me)) {
      showPermissionModal?.(rolesPermissionsMatrix.shareEwebinar.path);
      return;
    }
    toggleModal({
      type: HomePageModalType.ShareAll,
    });
  };

  const openShareModal = React.useCallback(
    (setId: string): void => {
      if (hasPermission(rolesPermissionsMatrix.shareEwebinar.path, me)) {
        toggleModal({
          setId,
          type: HomePageModalType.Share,
        });
      } else {
        showPermissionModal?.(rolesPermissionsMatrix.shareEwebinar.path);
      }
    },
    [me, showPermissionModal]
  );

  const openWidgetModal = React.useCallback((setId: string): void => {
    navigate(`/home/widget/${setId}`);
  }, []);

  const openCloneWidgetModal = React.useCallback((setId: string): void => {
    toggleModal({
      setId,
      type: HomePageModalType.CloneWebinar,
    });
  }, []);

  const closeModal = React.useCallback((): void => {
    toggleModal(undefined);
  }, []);

  const onSetLoaded = React.useCallback((setId: string) => {
    setLoadedSets((loadedSets) => ({
      ...loadedSets,
      [setId]: true,
    }));
  }, []);

  const noPublishedWebinars = allSets.filter((s) => s.group === EWebinarSetGroup.Published).length;

  const value = {
    allSets,
    filteredSets,
    loadedSets,
    noPublishedWebinars,
    modal,
    toggleModal,
    openShareAllModal,
    openShareModal,
    openWidgetModal,
    openCloneWidgetModal,
    closeModal,
    onSetLoaded,
  };

  return <HomePageContext.Provider value={value}>{children}</HomePageContext.Provider>;
};

export const useHomePageContext = (): HomePageContextValue => React.useContext(HomePageContext);
